// src/features/admin/adminSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

export const fetchAdminUsers = createAsyncThunk(
    "admin/fetchUsers",
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) {
            return thunkAPI.rejectWithValue("Not authenticated");
        }

        try {
            const res = await fetch(`${API_URL}/admin/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!res.ok) {
                const errorBody = await res.json().catch(() => ({}));
                const msg = errorBody.error || `Request failed with ${res.status}`;
                return thunkAPI.rejectWithValue(msg);
            }

            const data = await res.json();
            return data;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.message || "Network error");
        }
    }
);

// ✅ NEW: toggle active
export const updateUserActive = createAsyncThunk(
    "admin/updateUserActive",
    async ({ userId, isActive }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) {
            return thunkAPI.rejectWithValue("Not authenticated");
        }

        try {
            const res = await fetch(`${API_URL}/admin/users/${userId}/active`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ isActive }),
            });

            const body = await res.json().catch(() => ({}));

            if (!res.ok) {
                const msg = body.error || `Request failed with ${res.status}`;
                return thunkAPI.rejectWithValue(msg);
            }

            return body; // updated user
        } catch (err) {
            return thunkAPI.rejectWithValue(err.message || "Network error");
        }
    }
);


export const updateUserRole = createAsyncThunk(
    "admin/updateUserRole",
    async ({ userId, role }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) {
            return thunkAPI.rejectWithValue("Not authenticated");
        }

        try {
            const res = await fetch(`${API_URL}/admin/users/${userId}/role`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ role }),
            });

            const body = await res.json().catch(() => ({}));

            if (!res.ok) {
                const msg = body.error || `Request failed with ${res.status}`;
                return thunkAPI.rejectWithValue(msg);
            }

            return body; // updated user
        } catch (err) {
            return thunkAPI.rejectWithValue(err.message || "Network error");
        }
    }
);


export const assignStudentToTeacher = createAsyncThunk(
    "admin/assignStudentToTeacher",
    async ({ studentId, teacherId }, thunkAPI) => {
        const state = thunkAPI.getState();
        const token = state.auth.token;

        if (!token) {
            return thunkAPI.rejectWithValue("Not authenticated");
        }

        try {
            const res = await fetch(
                `${API_URL}/admin/teachers/${teacherId}/students/${studentId}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const body = await res.json().catch(() => ({}));

            if (!res.ok) {
                const msg = body.error || `Request failed with ${res.status}`;
                return thunkAPI.rejectWithValue(msg);
            }

            // backend returns { message, student: { ...updatedStudent } }
            return body.student;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.message || "Network error");
        }
    }
);


const adminSlice = createSlice({
    name: "admin",
    initialState: {
        users: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAdminUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload;
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Failed to load users";
            })
            // ✅ updateUserActive
            .addCase(updateUserActive.fulfilled, (state, action) => {
                const updated = action.payload;
                state.users = state.users.map((u) =>
                    u.id === updated.id ? updated : u
                );
            })
            .addCase(updateUserActive.rejected, (state, action) => {
                state.error = action.payload || "Failed to update user active status";
            })
            .addCase(updateUserRole.fulfilled, (state, action) => {
                const updated = action.payload;
                state.users = state.users.map((u) =>
                    u.id === updated.id ? updated : u
                );
            })
            .addCase(updateUserRole.rejected, (state, action) => {
                state.error = action.payload || "Failed to update user role";
            })
            .addCase(assignStudentToTeacher.fulfilled, (state, action) => {
                const updatedStudent = action.payload;
                state.users = state.users.map((u) =>
                    u.id === updatedStudent.id ? { ...u, ...updatedStudent } : u
                );
            })
            .addCase(assignStudentToTeacher.rejected, (state, action) => {
                state.error = action.payload || "Failed to assign student to teacher";
            });
    },
});

export default adminSlice.reducer;
